import { Suspense, useEffect, useMemo, useState } from 'react'

import { GearIcon } from '@radix-ui/react-icons'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  LinkProps,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import {
  AlignLeftIcon,
  BookmarkIcon,
  BriefcaseIcon,
  FileStackIcon,
  Globe2Icon,
  GraduationCapIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react'

import { queryAuthProfile } from '@/services/auth/query'
import { queryUserInformation } from '@/services/user/query'

import { Container } from '@/components/layout/container'
import { MainLayout } from '@/components/layout/main-layout'
import { SiteFooterUser } from '@/components/layout/site-footer'
import { SiteHeader } from '@/components/layout/site-header'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'

import { getFallbackName } from '@/lib/utils/utils'

import { cn } from '@/lib/cn'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const profile = await queryClient
      .ensureQueryData(queryAuthProfile())
      .catch(() => null)

    if (!profile) return redirect({ to: '/auth/login', replace: true })
    const isVerified = profile.getProfile.user.is_verified
    if (!isVerified)
      return redirect({ to: '/auth/verification/phone', replace: true })
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <MainLayout
      header={
        <>
          <SiteHeader typeNav="user" />
          <MobileSidebar />
        </>
      }
      footer={<SiteFooterUser />}
    >
      <Container className="flex grow flex-col lg:flex-row">
        <DesktopSidebar />
        <div className="flex grow flex-col py-6 lg:ml-6">
          <Suspense
            fallback={
              <div className="flex grow flex-col items-center justify-center">
                <Spinner className="bg-yes-600" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </Container>
    </MainLayout>
  )
}

function DesktopSidebar() {
  return (
    <div className="hidden w-64 shrink-0 lg:block">
      <div className="sticky top-[5.5rem] mb-3 overflow-y-auto rounded-lg bg-background shadow-md">
        <NavigationRails />
      </div>
    </div>
  )
}

function MobileSidebar() {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  const label = useMemo(() => {
    const link = Object.entries(linkProfiles).find(([key, val]) =>
      pathname.includes(val.to)
    )
    return link?.[0]
  }, [pathname])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <div className="sticky top-16 flex items-center border-b bg-background lg:hidden">
      <Container className="h-10">
        <button
          className="flex h-full items-center gap-2 pr-6 text-gray-500"
          type="button"
          onClick={() => setOpen(true)}
        >
          <AlignLeftIcon size={16} />
          <p className="text-sm">{label || 'Menu'}</p>
        </button>
      </Container>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="w-[18rem] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          side="left"
        >
          <div className="flex h-full w-full flex-col">
            <NavigationRails />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

const linkProfiles = {
  // 'Informasi Pribadi': {
  //   icon: <UserIcon size={16} />,
  //   to: '/dashboard/personal-information',
  // },
  Bahasa: {
    icon: <Globe2Icon size={16} />,
    to: '/dashboard/languages',
  },
  Pendidikan: {
    icon: <GraduationCapIcon size={16} />,
    to: '/dashboard/education',
  },
  'Riwayat Pekerjaan': {
    icon: <BriefcaseIcon size={16} />,
    to: '/dashboard/experience',
  },
  'Lisensi / Sertifikat': {
    icon: <FileStackIcon size={16} />,
    to: '/dashboard/license',
  },
} satisfies Record<string, { icon: React.ReactNode; to: LinkProps['to'] }>

const linkJobs = {
  'Job Saya': {
    icon: <BookmarkIcon size={16} />,
    to: '/dashboard/job-history',
  },
} satisfies Record<string, { icon: React.ReactNode; to: LinkProps['to'] }>
const linkAccount = {
  Pengaturan: {
    icon: <SettingsIcon size={16} />,
    to: '/account/edit',
  },
} satisfies Record<string, { icon: React.ReactNode; to: LinkProps['to'] }>

function NavigationRails() {
  const { pathname } = useLocation()
  const profileQry = useSuspenseQuery(queryAuthProfile())
  const { data: userPersonalInfo } = useSuspenseQuery(queryUserInformation())
  const { user } = profileQry.data.getProfile

  const fallbackName = getFallbackName(
    user?.name || userPersonalInfo?.name || user.email
  )

  return (
    <div className="flex flex-col">
      <div className="px-2 py-6">
        <Avatar className="m-auto size-16 border border-border shadow-sm">
          <AvatarImage
            src={userPersonalInfo?.photo}
            alt="Avatar"
            className="rounded-full"
          />
          <AvatarFallback>{fallbackName}</AvatarFallback>
        </Avatar>
        <div className="mt-6 space-y-2 rounded-lg text-center">
          <p className="line-clamp-1 text-sm font-semibold">{user.name}</p>
          <p className="text-xs">{user.email}</p>
        </div>
        <Link to="/dashboard/personal-information">
          <p className="mt-4 cursor-pointer text-center text-sm text-yes-600 underline underline-offset-1">
            Lihat Profil
          </p>
        </Link>
      </div>
      <div className="mt-2 pb-8">
        <h3 className="px-4 font-semibold text-gray-600">PROFIL SAYA</h3>
        <div className="mt-1">
          {Object.entries(linkProfiles).map(([label, { icon, to }]) => {
            const isActive = pathname.includes(to)
            return (
              <Link
                key={to}
                className={cn(
                  'flex items-center gap-2 px-4 py-3.5 transition-colors duration-300',
                  isActive ? 'text-yes-600' : 'text-gray-500 hover:bg-gray-200'
                )}
                to={to}
              >
                <div className="w-5">{icon}</div>
                <p className="line-clamp-1 text-sm">{label}</p>
              </Link>
            )
          })}
        </div>
      </div>
      <div className="mt-2 pb-8">
        <h3 className="px-4 font-semibold text-gray-600">AKTIFITAS</h3>
        <div className="mt-1">
          {Object.entries(linkJobs).map(([label, { icon, to }]) => {
            const isActive = pathname.includes(to)
            return (
              <Link
                key={to}
                className={cn(
                  'flex items-center gap-2 px-4 py-3.5 transition-colors duration-300',
                  isActive ? 'text-yes-600' : 'text-gray-500 hover:bg-gray-200'
                )}
                to={to}
              >
                <div className="w-5">{icon}</div>
                <p className="line-clamp-1 text-sm">{label}</p>
              </Link>
            )
          })}
        </div>
      </div>
      <div className="mt-2 pb-8">
        <h3 className="px-4 font-semibold text-gray-600">AKUN</h3>
        <div className="mt-1">
          {Object.entries(linkAccount).map(([label, { icon, to }]) => {
            const isActive = pathname.includes(to)
            return (
              <Link
                key={to}
                className={cn(
                  'flex items-center gap-2 px-4 py-3.5 transition-colors duration-300',
                  isActive ? 'text-yes-600' : 'text-gray-500 hover:bg-gray-200'
                )}
                to={to}
              >
                <div className="w-5">{icon}</div>
                <p className="line-clamp-1 text-sm">{label}</p>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
